import { forwardRef, type ComponentWithAs } from '@chakra-ui/react';
import React, {
  type ForwardRefExoticComponent,
  type RefAttributes,
} from 'react';

import { Text } from '@frontend/components';
import type { IconProps } from '@frontend/components';
import * as GDT_ICONS from '@frontend/components/src/components/mediaAndIcons/icons/iconSet/gdt';

import type { GDTSymbolTextProps } from './types';

// Direct mapping from unicode symbols to icon components
const UNICODE_TO_ICON: Record<
  string,
  ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>
> = {
  '⏤': GDT_ICONS.StraightnessIcon,
  '⏥': GDT_ICONS.FlatnessIcon,
  '○': GDT_ICONS.CircularityIcon,
  '⌭': GDT_ICONS.CylindricityIcon,
  '⌒': GDT_ICONS.ProfileLineIcon,
  '⌓': GDT_ICONS.ProfileSurfaceIcon,
  '∠': GDT_ICONS.AngularityIcon,
  '⏊': GDT_ICONS.PerpendicularityIcon,
  '⊥': GDT_ICONS.PerpendicularityIcon,
  '∥': GDT_ICONS.ParallelismIcon,
  '⫽': GDT_ICONS.ParallelismIcon,
  '⌖': GDT_ICONS.TruePositionIcon,
  '◎': GDT_ICONS.ConcentricityIcon,
  '⌯': GDT_ICONS.SymmetryIcon,
  '↗': GDT_ICONS.CircularRunoutIcon,
  '⌰': GDT_ICONS.TotalRunoutIcon,
  'Ⓕ': GDT_ICONS.FreeStateIcon,
  'Ⓛ': GDT_ICONS.LeastMaterialConditionIcon,
  'Ⓜ': GDT_ICONS.MaximumMaterialConditionIcon,
  'Ⓟ': GDT_ICONS.ProjectedToleranceZoneIcon,
  'Ⓢ': GDT_ICONS.StatisticalToleranceIcon,
  'Ⓣ': GDT_ICONS.TangentPlaneIcon,
  'Ⓤ': GDT_ICONS.UnequallyDisposedToleranceIcon,
  '<CF>': GDT_ICONS.ContinuousFeatureIcon,
  '<ST>': GDT_ICONS.SurfaceFinishIcon,
  '±': GDT_ICONS.PlusOrMinusIcon,
  '⌀': GDT_ICONS.DiameterIcon,
  ø: GDT_ICONS.DiameterIcon,
  '↧': GDT_ICONS.DepthIcon,
  '≥': GDT_ICONS.GreaterThanOrEqualToIcon,
  '≤': GDT_ICONS.LessThanOrEqualToIcon,
  '⌴': GDT_ICONS.CounterboreIcon,
  '⊔': GDT_ICONS.CounterboreIcon,
  '⌵': GDT_ICONS.CountersinkIcon,
  µ: GDT_ICONS.MicroIcon,
  thru: GDT_ICONS.ThruIcon,
  '✓': GDT_ICONS.SurfaceFinishIcon,
  '℄': GDT_ICONS.CenterlineIcon,
} as const;

// Symbol descriptions for accessibility
const SYMBOL_DESCRIPTIONS: Record<string, string> = {
  '⏤': 'straightness',
  '⏥': 'flatness',
  '○': 'circularity',
  '⌭': 'cylindricity',
  '⌒': 'profile of a line',
  '⌓': 'profile of a surface',
  '∠': 'angularity',
  '⏊': 'perpendicularity',
  '⊥': 'perpendicularity',
  '∥': 'parallelism',
  '⫽': 'parallelism',
  '⌖': 'true position',
  '◎': 'concentricity',
  '⌯': 'symmetry',
  '↗': 'circular runout',
  '⌰': 'total runout',
  'Ⓕ': 'free state',
  'Ⓛ': 'least material condition',
  'Ⓜ': 'maximum material condition',
  'Ⓟ': 'projected tolerance zone',
  'Ⓢ': 'statistical tolerance',
  'Ⓣ': 'tangent plane',
  'Ⓤ': 'unequally disposed tolerance',
  '<CF>': 'continuous feature',
  '<ST>': 'surface finish',
  '±': 'plus or minus',
  '⌀': 'diameter',
  ø: 'diameter',
  '↧': 'depth',
  '≥': 'greater than or equal to',
  '≤': 'less than or equal to',
  '⌴': 'counterbore',
  '⊔': 'counterbore',
  '⌵': 'countersink',
  µ: 'micro',
  thru: 'through',
  '✓': 'surface finish',
  '℄': 'centerline',
} as const;

// Pre-compiled regex pattern for symbol detection
const SYMBOL_PATTERN = (() => {
  const escapedSymbols = Object.keys(UNICODE_TO_ICON)
    .map((key) => key.replace(/[.*+?^${ }()|[\]\\]/g, '\\$&'))
    .join('|');
  return new RegExp(`(${escapedSymbols}|\\\\[${escapedSymbols}])`, 'g');
})();

// Style object for symbol wrapper
const SYMBOL_WRAPPER_STYLE = {
  '--size': '1.2em',
  display: 'inline-flex',
  height: 'var(--size)',
  position: 'relative',
} as const;

const processTextPart = (
  part: string,
): { text: string; isEscaped: boolean } => {
  if (part.startsWith('\\')) {
    return { text: part.slice(1), isEscaped: true };
  }
  return { text: part, isEscaped: false };
};

export const GDTSymbolText: ComponentWithAs<'p', GDTSymbolTextProps> =
  forwardRef<GDTSymbolTextProps, 'p'>(
    ({ children, iconProps = {}, ...textProps }, ref) => {
      const processedParts = (() => {
        if (!children) return [];

        return children
          .split(SYMBOL_PATTERN)
          .filter(Boolean)
          .map((part) => {
            const { text: processedText, isEscaped } = processTextPart(part);
            const Icon =
              UNICODE_TO_ICON[processedText as keyof typeof UNICODE_TO_ICON];

            return {
              originalPart: part,
              processedText,
              isEscaped,
              Icon,
            };
          });
      })();

      if (!children) {
        return (
          <Text
            ref={ref}
            as="span"
            display="inline-block"
            className="gdt-symbol-text"
            {...textProps}
          />
        );
      }

      return (
        <Text
          ref={ref}
          as="span"
          display="inline-block"
          className="gdt-symbol-text"
          {...textProps}
        >
          {processedParts.map(
            ({ originalPart, processedText, isEscaped, Icon }, index) => {
              // Create a stable key that doesn't rely on array index
              // For escaped symbols, we need to include the full context to make keys unique
              const keySuffix = isEscaped
                ? `escaped-${processedText}-${index}`
                : originalPart.length > 0
                ? originalPart
                : `empty-${index}`;
              // NOTE: we have to use index here since there is not guarantee the separated
              //  pieces of text will be different
              const symbolKey = `symbol-${processedText}-${keySuffix}-${index}`;
              const textKey = `text-${keySuffix}-${index}`;

              if (Icon && !isEscaped) {
                const symbolDescription =
                  SYMBOL_DESCRIPTIONS[
                    processedText as keyof typeof SYMBOL_DESCRIPTIONS
                  ];
                const accessibilityLabel = symbolDescription
                  ? `${symbolDescription} symbol`
                  : undefined;

                return (
                  <span
                    className="gdt-symbol-text__symbol-wrapper"
                    style={SYMBOL_WRAPPER_STYLE}
                    key={symbolKey}
                    role="img"
                    aria-label={accessibilityLabel}
                  >
                    <Text
                      as="span"
                      color="transparent"
                      position="absolute"
                      size={textProps?.size}
                      className="gdt-symbol-text__symbol-placeholder"
                      sx={{
                        '::selection': {
                          color: 'transparent',
                        },
                      }}
                    >
                      {processedText}
                    </Text>
                    <Icon
                      position="relative"
                      className={`gdt-symbol-text__icon gdt-symbol-text__icon--${processedText}`}
                      // 3px is a magic number that nicely aligns the gdt symbols
                      // across all text sizes. Why 3px? Not sure.
                      top="3px"
                      // Only define the height to let wider GDT like <ST> be wide
                      height="var(--size)"
                      {...iconProps}
                    />
                  </span>
                );
              }
              return (
                <React.Fragment key={textKey}>{processedText}</React.Fragment>
              );
            },
          )}
        </Text>
      );
    },
  );

GDTSymbolText.displayName = 'GDTSymbolText';
